import { useState } from "react";
import { Lock, Eye, EyeOff, CheckCircle, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export function SetPasswordModal({ open, onClose, onSuccess, token }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validatePassword = (pwd) => {
    const validations = {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /[0-9]/.test(pwd),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(pwd)
    };
    return validations;
  };

  const passwordValidations = validatePassword(password);
  const isPasswordStrong = Object.values(passwordValidations).filter(v => v).length >= 4;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (password.length < 8) {
      setErrors({ password: "Password must be at least 8 characters" });
      return;
    }

    if (!isPasswordStrong) {
      setErrors({ password: "Password is not strong enough" });
      return;
    }

    if (password !== confirmPassword) {
      setErrors({ confirmPassword: "Passwords do not match" });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/api/hospital/set-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          new_password: password,
          confirm_password: confirmPassword
        })
      });

      if (response.ok) {
        toast.success("Password set successfully! You can now login with your email and password.");
        onSuccess();
        onClose();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to set password");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Create Your Password
          </DialogTitle>
          <DialogDescription>
            Set a permanent password for your account. You'll use this to login in the future.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10"
                placeholder="Enter new password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-red-500">{errors.password}</p>
            )}
          </div>

          {/* Password strength indicators */}
          <div className="bg-slate-50 rounded-lg p-3 space-y-2">
            <p className="text-xs font-medium text-slate-600 mb-2">Password requirements:</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className={`flex items-center gap-1.5 ${passwordValidations.length ? 'text-green-600' : 'text-slate-400'}`}>
                <CheckCircle className={`w-3.5 h-3.5 ${passwordValidations.length ? 'opacity-100' : 'opacity-40'}`} />
                8+ characters
              </div>
              <div className={`flex items-center gap-1.5 ${passwordValidations.uppercase ? 'text-green-600' : 'text-slate-400'}`}>
                <CheckCircle className={`w-3.5 h-3.5 ${passwordValidations.uppercase ? 'opacity-100' : 'opacity-40'}`} />
                Uppercase letter
              </div>
              <div className={`flex items-center gap-1.5 ${passwordValidations.lowercase ? 'text-green-600' : 'text-slate-400'}`}>
                <CheckCircle className={`w-3.5 h-3.5 ${passwordValidations.lowercase ? 'opacity-100' : 'opacity-40'}`} />
                Lowercase letter
              </div>
              <div className={`flex items-center gap-1.5 ${passwordValidations.number ? 'text-green-600' : 'text-slate-400'}`}>
                <CheckCircle className={`w-3.5 h-3.5 ${passwordValidations.number ? 'opacity-100' : 'opacity-40'}`} />
                Number
              </div>
              <div className={`flex items-center gap-1.5 ${passwordValidations.special ? 'text-green-600' : 'text-slate-400'}`}>
                <CheckCircle className={`w-3.5 h-3.5 ${passwordValidations.special ? 'opacity-100' : 'opacity-40'}`} />
                Special character
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10 pr-10"
                placeholder="Confirm new password"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-red-500">{errors.confirmPassword}</p>
            )}
            {confirmPassword && password === confirmPassword && (
              <p className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Passwords match
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700"
            disabled={loading || !isPasswordStrong || password !== confirmPassword}
          >
            {loading ? "Setting Password..." : "Set Password & Continue"}
          </Button>

          <p className="text-xs text-center text-slate-500">
            You'll use this password along with your email to login in the future.
            If you forget it, you can request a new access code.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default SetPasswordModal;
